import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/apiClient";
import { Sparkles, Send, Star } from "lucide-react";
import { toast } from "sonner";

const SENTIMENT_CLS = {
  positive: "bg-emerald-500/10 text-emerald-300 border-emerald-500/20",
  neutral: "bg-white/5 text-white/60 border-white/10",
  negative: "bg-orange-500/10 text-orange-300 border-orange-500/20",
};

function Stars({ n }) {
  return (
    <div className="flex gap-0.5 text-[#D4AF37]">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className="w-3.5 h-3.5" fill={i <= n ? "currentColor" : "none"} strokeWidth={1.5} />
      ))}
    </div>
  );
}

export default function Reviews() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [replies, setReplies] = useState({});
  const [busy, setBusy] = useState({});

  async function load() {
    const { data } = await api.get("/reviews");
    setData(data);
    const initial = {};
    data.items.forEach(r => { initial[r.id] = r.reply || ""; });
    setReplies(initial);
  }

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (filter === "all") return data.items;
    if (filter === "unreplied") return data.items.filter(r => !r.reply);
    return data.items.filter(r => r.sentiment === filter);
  }, [data, filter]);

  async function saveReply(id) {
    const reply = (replies[id] || "").trim();
    if (!reply) return;
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await api.post(`/reviews/${id}/reply`, { reply });
      toast.success("Reply posted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy((b) => ({ ...b, [id]: false })); }
  }

  async function aiSuggest(id) {
    setBusy((b) => ({ ...b, [`ai_${id}`]: true }));
    try {
      const { data } = await api.post(`/reviews/${id}/ai-suggest-reply`);
      setReplies((r) => ({ ...r, [id]: data.suggested_reply }));
      toast.success("Cerebrum drafted a reply");
    } catch (e) { toast.error(e.response?.data?.detail || "AI failed"); }
    finally { setBusy((b) => ({ ...b, [`ai_${id}`]: false })); }
  }

  if (!data) return <div className="text-white/50">Loading reviews…</div>;

  const s = data.summary;
  const filters = [
    { k: "all", label: `All · ${s.total}` },
    { k: "unreplied", label: `Unreplied · ${data.items.filter(r => !r.reply).length}` },
    { k: "positive", label: `Positive · ${s.by_sentiment.positive || 0}` },
    { k: "neutral", label: `Neutral · ${s.by_sentiment.neutral || 0}` },
    { k: "negative", label: `Negative · ${s.by_sentiment.negative || 0}` },
  ];

  return (
    <div className="space-y-8" data-testid="reviews-page">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Reputation</div>
        <h1 className="font-display text-3xl md:text-4xl">Reviews inbox</h1>
      </div>

      <div className="grid md:grid-cols-4 gap-5">
        <div className="bg-[#12182B] border border-white/5 rounded-xl p-5" data-testid="rev-sum-avg">
          <div className="text-xs uppercase tracking-widest text-white/50">Average rating</div>
          <div className="flex items-end gap-3 mt-3">
            <div className="font-display text-4xl">{s.avg_rating}</div>
            <Stars n={Math.round(s.avg_rating)} />
          </div>
          <div className="text-xs text-white/40 mt-2">across {s.total} reviews</div>
        </div>
        {Object.entries(s.by_source || {}).slice(0, 3).map(([src, n]) => (
          <div key={src} className="bg-[#12182B] border border-white/5 rounded-xl p-5" data-testid={`rev-src-${src}`}>
            <div className="text-xs uppercase tracking-widest text-white/50">{src}</div>
            <div className="font-display text-4xl mt-3">{n}</div>
            <div className="text-xs text-white/40 mt-2">reviews</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {filters.map((f) => (
          <button key={f.k} onClick={() => setFilter(f.k)}
            className={`text-xs uppercase tracking-widest px-3 py-2 rounded-md border transition ${filter === f.k ? "bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30" : "bg-transparent text-white/60 border-white/10 hover:text-white"}`}
            data-testid={`rev-filter-${f.k}`}>{f.label}</button>
        ))}
      </div>

      <div className="space-y-4">
        {filtered.map((r) => (
          <div key={r.id} className="bg-[#12182B] border border-white/5 rounded-xl p-6" data-testid={`review-${r.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <span className="font-medium">{r.author}</span>
                  <span className="text-xs text-white/40">· {r.source}</span>
                  <span className={`text-[10px] tracking-widest uppercase px-2 py-0.5 rounded border ${SENTIMENT_CLS[r.sentiment] || SENTIMENT_CLS.neutral}`}>{r.sentiment}</span>
                </div>
                <Stars n={r.rating} />
                <p className="text-white/80 leading-relaxed mt-3">{r.text}</p>
              </div>
              <div className="text-xs text-white/40 shrink-0">{new Date(r.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</div>
            </div>

            {r.reply && (
              <div className="mt-4 border-l-2 border-[#D4AF37]/40 pl-4">
                <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">Your reply · {new Date(r.reply_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</div>
                <p className="text-white/70 text-sm">{r.reply}</p>
              </div>
            )}

            <div className="mt-4 flex flex-col gap-3">
              <textarea
                value={replies[r.id] || ""}
                onChange={(e) => setReplies({ ...replies, [r.id]: e.target.value })}
                placeholder={r.reply ? "Update your reply…" : "Write a reply to this guest…"}
                rows={3}
                className="w-full bg-[#0a0f1c] border border-white/10 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4AF37]/60 resize-none"
                data-testid={`reply-input-${r.id}`}
              />
              <div className="flex gap-2 justify-end">
                <button onClick={() => aiSuggest(r.id)} disabled={busy[`ai_${r.id}`]} className="btn-ghost-gold rounded-md px-4 py-2 text-sm inline-flex items-center gap-2" data-testid={`ai-suggest-${r.id}`}>
                  <Sparkles className="w-3.5 h-3.5" />
                  {busy[`ai_${r.id}`] ? "Cerebrum drafting…" : "AI suggest"}
                </button>
                <button onClick={() => saveReply(r.id)} disabled={busy[r.id] || !replies[r.id]} className="btn-gold rounded-md px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-60" data-testid={`reply-send-${r.id}`}>
                  <Send className="w-3.5 h-3.5" /> {busy[r.id] ? "Posting…" : r.reply ? "Update" : "Reply"}
                </button>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="bg-[#12182B] border border-white/5 rounded-xl p-10 text-center text-white/50">No reviews match this filter.</div>}
      </div>
    </div>
  );
}
